import {
  CLOUDINARY_DEFAULT_IMAGE_COMPRESSION,
  CLOUDINARY_DEFAULT_IMAGE_CROP,
  CLOUDINARY_VIDEO_EXTENSION_TYPES,
} from "@/constants.ts";

// The formats we ask Cloudinary to transcode to. Both must be producible from any source video.
const VIDEO_DELIVERY_FORMAT = "mp4";
const VIDEO_POSTER_FORMAT = "jpg";

// Includes the delivery format itself, so an already-playable url is never rewritten.
const BROWSER_PLAYABLE_VIDEO_FORMATS = [VIDEO_DELIVERY_FORMAT, "webm", "ogg"];

const VIDEO_RESOURCE_SEGMENT = "/video/upload/";
const IMAGE_RESOURCE_SEGMENT = "/image/upload/";

/**
 * Function that add extra params to Cloudinary image url for image transformation
 *
 * @param {Object} parameters { path, compress, aspectRatio, crop, width, height, widthPortion, heightPortion }
 * @returns {String}
 */
export const imageTransformation = (parameters) => {
  if (!(!!parameters && parameters.constructor === Object)) {
    return "";
  }

  const getWidthPortionParameters = (parameters) => {
    const wp = parameters?.widthPortion;
    if (wp && Number(wp) > 0 && !Number.isInteger(wp)) {
      return `w_${wp}`;
    }

    return undefined;
  };

  const getHeightPortionParameters = (parameters) => {
    const hp = parameters?.heightPortion;
    if (hp && Number(hp) > 0 && !Number.isInteger(hp)) {
      return `h_${hp}`;
    }

    return undefined;
  };

  const params = (
    [
      // Compress
      parameters?.compress ? CLOUDINARY_DEFAULT_IMAGE_COMPRESSION : undefined,
      // Aspect Ratio
      parameters?.aspectRatio ? `ar_${parameters.aspectRatio}` : undefined,
      // Crop
      parameters?.crop ? CLOUDINARY_DEFAULT_IMAGE_CROP : undefined,
      // Width
      parameters?.width && Number(parameters?.width) > 0
        ? `w_${parameters?.width}`
        : getWidthPortionParameters(parameters),
      // Height
      parameters?.height && Number(parameters?.height) > 0
        ? `h_${parameters?.height}`
        : getHeightPortionParameters(parameters),
    ].filter((item) => item) || []
  ).join();

  if (params && parameters?.path) {
    const upload = "upload";
    const splitedPath = parameters?.path.split(`${upload}/`);
    splitedPath.splice(1, 0, `${upload}/${params}/`);
    return splitedPath.join("");
  }
  return parameters?.path ?? "";
};

/**
 * Parse a media url. Parsed rather than split on `[?#]`, since a query such as `?v=1.2`
 * holds a later dot that would otherwise be mistaken for the extension.
 *
 * @param {String} mediaUrl
 * @returns {URL|undefined} undefined for anything that is not a parseable absolute url
 */
const parseMediaUrl = (mediaUrl) => {
  try {
    return new URL(mediaUrl);
  } catch {
    return undefined;
  }
};

/**
 * Extract the lowercased file extension from a Cloudinary media url.
 *
 * @param {String} mediaUrl
 * @returns {String} extension without the dot, or "" when there is none
 */
const getMediaExtension = (mediaUrl) => {
  const url = parseMediaUrl(mediaUrl);

  if (!url) {
    return "";
  }

  const lastSegment = url.pathname.slice(url.pathname.lastIndexOf("/") + 1);
  const dotIndex = lastSegment.lastIndexOf(".");

  return dotIndex < 0 ? "" : lastSegment.slice(dotIndex + 1).toLowerCase();
};

/**
 * Swap a media url's extension, dropping any query and hash since Cloudinary serves the
 * swapped asset from the path alone.
 *
 * @param {String} mediaUrl
 * @param {String} extension without the dot
 * @returns {String|undefined} undefined when the url carries no extension to swap
 */
const withExtension = (mediaUrl, extension) => {
  const url = parseMediaUrl(mediaUrl);

  // A non-empty extension means the url parsed, so url is set here.
  if (!getMediaExtension(mediaUrl)) {
    return undefined;
  }

  url.pathname = `${url.pathname.slice(0, url.pathname.lastIndexOf("."))}.${extension}`;
  url.search = "";
  url.hash = "";

  return url.toString();
};

/**
 * Whether a media url points at a video. Cloudinary stamps the resource type into the url
 * at upload, so it is authoritative where an extension allowlist misses formats like .mkv.
 *
 * @param {String} mediaUrl
 * @returns {Boolean}
 */
export const isVideoMedia = (mediaUrl) => {
  if (!mediaUrl || typeof mediaUrl !== "string") {
    return false;
  }

  // Media is always stored as a Cloudinary secure_url, so anything that is not an absolute url
  // is not ours to classify. Read the resource type from the path, so a query mentioning the
  // other type does not win.
  const url = parseMediaUrl(mediaUrl);

  if (!url) {
    return false;
  }

  if (url.pathname.includes(VIDEO_RESOURCE_SEGMENT)) {
    return true;
  }

  if (url.pathname.includes(IMAGE_RESOURCE_SEGMENT)) {
    return false;
  }

  return CLOUDINARY_VIDEO_EXTENSION_TYPES.includes(getMediaExtension(mediaUrl));
};

/**
 * Whether a media url should be rendered as an image.
 *
 * @param {String} mediaUrl
 * @returns {Boolean}
 */
export const isImageMedia = (mediaUrl) => Boolean(mediaUrl) && !isVideoMedia(mediaUrl);

/**
 * The video a single-slot surface should play. A video wins over an image regardless of
 * its position in the list.
 *
 * @param {Array} mediaList
 * @returns {String|undefined} undefined when the list holds no video
 */
export const firstVideoMedia = (mediaList) =>
  Array.isArray(mediaList) ? mediaList.find((item) => isVideoMedia(item)) : undefined;

/**
 * A video's still frame, which Cloudinary serves when the extension is swapped for an image one.
 * https://cloudinary.com/documentation/video_manipulation_and_delivery#video_thumbnails
 *
 * @param {String} videoUrl
 * @returns {String|undefined} undefined for a non-video, or a video url with no extension to swap
 */
export const videoPosterUrl = (videoUrl) => {
  if (!isVideoMedia(videoUrl)) {
    return undefined;
  }

  return withExtension(videoUrl, VIDEO_POSTER_FORMAT);
};

/**
 * Ask Cloudinary to deliver a video in a format the browser can play, by swapping the
 * extension to make it transcode on delivery.
 * https://cloudinary.com/documentation/video_manipulation_and_delivery#delivering_in_a_different_format
 * https://github.com/videojs/video.js/issues/5938
 *
 * @param {String} video
 * @returns String
 */
export const convertToMp4 = (video) => {
  if (!video || typeof video !== "string") {
    return "";
  }

  if (!isVideoMedia(video)) {
    return video;
  }

  const extension = getMediaExtension(video);

  if (!extension || BROWSER_PLAYABLE_VIDEO_FORMATS.includes(extension)) {
    return video;
  }

  return withExtension(video, VIDEO_DELIVERY_FORMAT) ?? video;
};
